<template>
  <div class="card">
    <h6>YOUR NEW HOME</h6>
    <div class="form-group _margin-bottom-30">
      <div class="col-sm-7">
        <h4>Enter the postcode of the new property</h4>
        <validation-messages label="Postcode" :$v="$v.postcode">
          <div class="input-group right">
            <form-field id="postcode" type="nospace" v-model="quote.postcode" :error="$v.postcode.$error"
              @change="onReset(); $refs.address.onReset()" />

            <div class="input-group-btn">
              <uiv-btn type="primary" :disabled="addressLoader" @click="onGetAddress(quote.postcode)">
                <loader :show="addressLoader" />Search
              </uiv-btn>
            </div>
          </div>
        </validation-messages>
      </div>
    </div>

    <div class="form-group address" v-show="addressList.length">
      <div class="col-sm-7">
        <loader :show="loading"></loader>
        <form-label id="searchAddress" v-show="!selectedAddress">Select your address</form-label>

        <validation-messages label="Address" :$v="$v.address">
          <search-select-list ref="address" id="searchAddress" :select-list="addressList" label-key="text"
            :error="$v.address.$error" @onChoose="onChoose" @onReset="onReset">
            {{ selectedAddress }}
          </search-select-list>
        </validation-messages>
      </div>
    </div>

    <uiv-alert class="_margin-top-20" type="danger"
      v-if="selectedAddress && !xoserveMetersLoader && $v.address.domesticMeter == false">
      <b>Oops! </b> <br />
      We are unable to proceed with your request as the meter at the entered address is registered as a commercial
      or non-domestic energy meter. We cannot process non domestic metered properties.
    </uiv-alert>

    <uiv-alert class="_margin-top-20" type="danger" v-if="selectedAddress && $v.address.prepayment == false">
      Oops! We are unable to proceed with your request as the meter at the entered address is identified as a
      Pre-Payment meter. We cannot process Pre-Payment metered properties.
    </uiv-alert>

    <div class="_margin-top-20 _margin-bottom-10">
      <small>
        I have read the <a :href="appSettings.supplierTermsAndConditions" target="_blank">Terms & Conditions</a>/<a
          class="padding-left" :href="appSettings.supplierPrivacyPolicy" target="_blank">Privacy Policy</a>
      </small>
    </div>

    <small>
      By continuing, you grant access to your gas or electricity meter information from the national energy industry
      database. This access is necessary to provide you with accurate quotes.
    </small>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';

export default {
  name: 'YourHome',
  data() {
    return {
      selectedAddress: null,
      noMpanOrMprn: false,
      loading: false
    };
  },
  props: {
    $v: Object
  },
  computed: {
    ...mapState({
      appSettings: ({ app_settings }) => app_settings,
      quote: ({ quote }) => quote,
      addressList: ({ lookups }) => lookups.addresses,
      addressLoader: ({ lookups }) => lookups.requesting.addresses,
      xoserveMetersLoader: ({ lookups }) => lookups.requesting.xoserveMeters
    })
  },
  methods: {
    ...mapActions({
      getAddresses: 'lookups/getAddresses',
      getXoserveMeters: 'lookups/getXoserveMeters',
      resetList: 'lookups/resetList',
      getSuppliers: 'lookups/getSuppliers',
      getTariffs: 'lookups/getTariffs'
    }),
    onReset() {
      this.quote.address = {};
      this.selectedAddress = null;
      this.quote.postcode = this.quote.postcode.toUpperCase();

      this.quote.usage = null;
    },
    onGetAddress(postcode) {
      this.$refs.address.onReset();
      this.$v.postcode.$touch();

      if (this.$v.postcode.$invalid) return;

      this.$v.$reset();

      this.getAddresses(postcode);
    },
    async onChoose(selected) {
      if (process.env.VUE_APP_GB_DATA == 'true') {
        this.selectedAddress = selected.text;
        this.quote.address = selected;

        const { mpan, mprn } = selected.parts;

        this.noMpanOrMprn = !mpan || !mprn;
        this.$v.address.$reset();
      } else {
        this.selectedAddress = selected.text;

        this.loading = true;
        const xoserveMeters = await this.getXoserveMeters(selected);
        const { mpan, mprn } = xoserveMeters;

        this.quote.address = {
          parts: { ...selected, ...xoserveMeters },
          text: selected.text
        };
        this.$v.address.$touch();

        if (!this.$v.address.$error) {
          await this.setDefaults();
        }

        this.noMpanOrMprn = !mpan || !mprn;
        this.$v.address.$reset();

        this.loading = false;
      }
    },
    async setDefaults() {
      let { gasdetails, elecdetails } = this.quote.address.parts;

      gasdetails = gasdetails.find(({ isDomestic }) => isDomestic) || {};
      elecdetails = elecdetails.find(({ isDomestic }) => isDomestic) || {};
      gasdetails.mugSupplierId = gasdetails.mugSupplierId ? 2 : gasdetails.mugSupplierId;
      elecdetails.mugSupplierId = elecdetails.mugSupplierId ? 2 : elecdetails.mugSupplierId;

      const twoSuppliers = gasdetails.mugSupplierId != null && elecdetails.mugSupplierId != null;
      this.quote.supplierId = gasdetails.mugSupplierId || elecdetails.mugSupplierId;
      this.quote.gasUsage.usage = 11500;
      this.quote.gasUsage.usageType = 1;
      this.quote.gasUsage.usageUnit = 1;
      this.quote.electricityUsage.usageType = 1;
      this.quote.electricityUsage.usageUnit = 1;
      this.quote.currentPayMethod = 'monthlyDirectDebit';
      this.quote.electricityCurrentPayMethod = 'monthlyDirectDebit';
      this.quote.gasCurrentPayMethod = 'monthlyDirectDebit';
      this.quote.gasSupplierId = gasdetails.mugSupplierId;
      this.quote.electricitySupplierId = elecdetails.mugSupplierId;
      this.quote.electricitySmartMeterInstalled = elecdetails.isSmartMeter;
      this.quote.gasSmartMeterInstalled = gasdetails.isSmartMeter;
      this.quote.newTariffPaymentMethod = 'monthlyDirectDebit';
      this.quote.sameSupplier = gasdetails.mugSupplierId == elecdetails.mugSupplierId ? 'yes' : 'no';
      this.quote.electricityUsage.disabledUnits = false;
      this.quote.electricityUsage.usage = 2700;

      if (twoSuppliers) {
        this.quote.compareFuel = 'gasElectricity';
        this.quote.address.parts.fuelType = 'gasElectricity';
      } else {
        this.quote.compareFuel = gasdetails.mugSupplierId ? 'gas' : 'electricity';
        this.quote.address.parts.fuelType = gasdetails.mugSupplierId ? 'gas' : 'electricity';
      }

      if (this.quote.compareFuel == 'gasElectricity' && this.quote.sameSupplier == 'yes') {
        if (elecdetails.isPrepayment == true && gasdetails.isPrepayment == true) {
          this.quote.currentPayMethod = 'prePaymentMeter';
        }
      }

      if ((this.quote.compareFuel == 'gasElectricity' && this.quote.sameSupplier == 'no') || this.quote.compareFuel == 'gas') {
        if (gasdetails.isPrepayment == true) {
          this.quote.gasCurrentPayMethod = 'prePaymentMeter';
        }
        this.quote.electricityUsage.disabledUnits = true;
      }

      if ((this.quote.compareFuel == 'gasElectricity' && this.quote.sameSupplier == 'no') || this.quote.compareFuel == 'electricity') {
        if (elecdetails.isPrepayment == true) {
          this.quote.electricityCurrentPayMethod = 'prePaymentMeter';
        }
      }

      if (this.quote.compareFuel == 'electricity') {
        this.quote.heatingFuel = 2;
        this.quote.cookingFuel = 2;

        const tariffs = await this.getTariffs({ fuelType: 'electricity', supplierId: this.quote.electricitySupplierId });
        const defaultTariffFound = tariffs.find(({ isDefaultOrStandard }) => isDefaultOrStandard);
        this.quote.electricityTariff = { ...defaultTariffFound };
        return
      }

      if (this.quote.compareFuel == 'gas') {
        const tariffs = await this.getTariffs({ fuelType: 'gas', supplierId: this.quote.gasSupplierId });
        const defaultTariffFound = tariffs.find(({ isDefaultOrStandard }) => isDefaultOrStandard);
        this.quote.electricityTariff = { ...defaultTariffFound };
        return
      }

      if (this.quote.compareFuel == 'gasElectricity' && this.quote.sameSupplier == 'yes') {
        const tariffs = await this.getTariffs({ fuelType: this.quote.compareFuel, supplierId: this.quote.supplierId });
        const defaultTariffFound = tariffs.find(({ isDefaultOrStandard }) => isDefaultOrStandard);
        this.quote.tariff = { ...defaultTariffFound };
      } else {
        const tariffsElectricity = await this.getTariffs({ fuelType: 'electricity', supplierId: this.quote.electricitySupplierId });
        const defaultTariffFoundE = tariffsElectricity.find(({ isDefaultOrStandard }) => isDefaultOrStandard);
        this.quote.electricityTariff = { ...defaultTariffFoundE };
        const tariffsGas = await this.getTariffs({ fuelType: 'gas', supplierId: this.quote.gasSupplierId });
        const defaultTariffFoundG = tariffsGas.find(({ isDefaultOrStandard }) => isDefaultOrStandard);
        this.quote.gasTariff = { ...defaultTariffFoundG };
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.address {
  .col-sm-7 {
    position: relative;

    .loader {
      position: absolute;
      bottom: 15px;
      left: 30px;
      width: 25px;
      height: 25px;
    }
  }
}
</style>
